import { useState } from 'react';
import { Box, Stack, Typography, Button, IconButton, Tooltip, alpha, useMediaQuery, useTheme } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import { GustavoBurbuja } from './GustavoBurbuja';
import { CRM_PALETTE as P } from '../../theme/brandPalette';
import type { Anuncio } from '../../store/api/anunciosApi';

/**
 * Una lámina de anuncio, a pantalla completa y cerrable.
 *
 * El acento se lee EN RENDER y no a nivel módulo: `applyCrmAccent` muta `CRM_PALETTE`
 * in-place con el color del tenant recién al renderizar el CRM logueado, así que un
 * `const X = P.brand.golden` arriba del archivo se queda con el color viejo. Es el mismo
 * gotcha documentado en el onboarding de Gustavo.
 *
 * UN SOLO ANCHO PARA TODO (`ANCHO`). La primera versión daba a cada bloque el suyo —la
 * bajada 460, el destacado el ancho completo, los puntos otro— y con el texto centrado
 * y las viñetas alineadas a la izquierda el eje de lectura se movía en cada bloque: se
 * veía torcida sin que se pudiera señalar qué estaba mal. Todo cuelga de la misma
 * columna y la simetría sale sola.
 */
const ac  = () => P.brand.golden;
const acL = () => P.brand.goldenLight;
const em  = (a: number) => alpha(ac(), a);

const MBox = motion(Box);
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
const ANCHO = 440;
const TXT = '#F2F5F8';

export function AvisoLamina({ aviso, onCerrar }: {
  aviso: Anuncio;
  /** `abrirGustavo` = cerró por el CTA, no por "entendido". */
  onCerrar: (abrirGustavo: boolean, irA?: string) => void;
}) {
  const theme = useTheme();
  const chico = useMediaQuery(theme.breakpoints.down('sm'));
  const reduced = useReducedMotion();
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    if (!aviso.destacado) return;
    try {
      await navigator.clipboard.writeText(aviso.destacado.valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch { /* sin portapapeles (http, permisos): el valor está a la vista igual */ }
  };

  /** Todos los botones con el mismo ancho mínimo: dos pills de largo distinto se leen torcidas. */
  const botonSx = {
    textTransform: 'none' as const, borderRadius: 999,
    px: 3, py: 1.15, minWidth: chico ? '100%' : 176,
  };

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 2000,
      bgcolor: '#070a09', color: TXT,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: { xs: 2.5, sm: 4 }, py: { xs: 4, sm: 5 }, overflowY: 'auto',
    }}>
      <MBox aria-hidden sx={{
        position: 'absolute', top: '-25%', left: '50%', width: 900, height: 900,
        transform: 'translateX(-50%)', borderRadius: '50%',
        background: `radial-gradient(circle, ${em(0.16)}, transparent 62%)`, pointerEvents: 'none',
      }}
        animate={reduced ? undefined : { opacity: [0.6, 1, 0.6], scale: [1, 1.06, 1] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} />

      <MBox
        initial={reduced ? undefined : { opacity: 0, y: 18 }}
        animate={reduced ? undefined : { opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: ANCHO, mx: 'auto' }}
      >
        {/* ── Encabezado ────────────────────────────────────────────────────── */}
        <Stack alignItems="center" spacing={2}>
          {aviso.figura === 'gustavo' ? (
            // La MISMA figura del botón flotante: el recorte sin fondo asomándose de la
            // burbuja, con la luz girando sobre el borde. Es la imagen con la que lo
            // reconocen — no la foto cuadrada. Toma el color de la marca igual que el FAB.
            <GustavoBurbuja size={148} />
          ) : aviso.figuraUrl ? (
            /**
             * Imagen propia del anuncio.
             *
             * Va CONTENIDA y no recortada —quien la sube eligió esa imagen, y un `cover` le
             * comería el logo o el título según la proporción— y toma el ANCHO de la lámina
             * en vez de un cuadrado chico: lo que se sube acá suele ser una portada 16:9, y
             * con un tope de 148px de lado quedaba como una estampilla ilegible.
             *
             * El `maxHeight` es lo que impide que una imagen vertical empuje el título y
             * los botones fuera de pantalla en un celular.
             */
            <Box
              component="img" src={aviso.figuraUrl} alt=""
              sx={{
                width: '100%', maxHeight: { xs: 200, sm: 240 }, objectFit: 'contain',
                borderRadius: 2.5,
              }}
            />
          ) : (
            <Box sx={{
              width: 56, height: 56, borderRadius: '50%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              bgcolor: em(0.14), border: `1px solid ${alpha(acL(), 0.35)}`,
            }}>
              <CampaignRounded sx={{ fontSize: 28, color: ac() }} />
            </Box>
          )}

          <Stack alignItems="center" spacing={1.25} sx={{ textAlign: 'center' }}>
            <Typography sx={{
              fontSize: 10.5, fontWeight: 800, letterSpacing: '0.22em',
              textTransform: 'uppercase', color: ac(),
            }}>{aviso.eyebrow}</Typography>

            <Typography component="h2" sx={{
              fontWeight: 800, lineHeight: 1.14, letterSpacing: '-0.02em',
              fontSize: { xs: 26, sm: 32 }, textWrap: 'balance',
            }}>{aviso.titulo}</Typography>

            {aviso.bajada && (
              <Typography sx={{
                fontSize: { xs: 14, sm: 14.5 }, lineHeight: 1.6, color: alpha(TXT, 0.68),
              }}>{aviso.bajada}</Typography>
            )}
          </Stack>
        </Stack>

        {/* ── El dato que se tienen que llevar ──────────────────────────────── */}
        {aviso.destacado && (
          <Box sx={{
            mt: 3, py: aviso.destacado.etiqueta ? 2 : 1.75, px: 2.5, borderRadius: 3, textAlign: 'center',
            border: `1px solid ${alpha(acL(), 0.4)}`, bgcolor: em(0.09),
          }}>
            {aviso.destacado.etiqueta && (
              <Typography sx={{
                fontSize: 10, fontWeight: 800, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: alpha(TXT, 0.55), mb: 1,
              }}>{aviso.destacado.etiqueta}</Typography>
            )}
            {/* El botón de copiar va en posición ABSOLUTA respecto del renglón: metido en
                el flex corría el número hacia la izquierda y el bloque quedaba descentrado
                justo en lo que más se mira. */}
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography sx={{
                fontWeight: 800, color: acL(), letterSpacing: '0.01em',
                fontSize: { xs: 24, sm: 30 }, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2,
              }}>{aviso.destacado.valor}</Typography>
              {aviso.destacado.copiable && (
                <Tooltip title={copiado ? '¡Copiado!' : 'Copiar'}>
                  <IconButton onClick={copiar} size="small" aria-label="Copiar" sx={{
                    position: 'absolute', right: -6, top: '50%', transform: 'translateY(-50%)',
                    color: copiado ? '#34D399' : alpha(TXT, 0.6),
                    '&:hover': { color: copiado ? '#34D399' : TXT },
                  }}>
                    {copiado ? <CheckRounded fontSize="small" /> : <ContentCopyRounded fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}

        {/* ── Los puntos ───────────────────────────────────────────────────────
            Alineados a la izquierda —una lista centrada no se lee— pero dentro de la
            misma columna que todo lo demás, así el bloque no se despega del eje. */}
        {!!aviso.puntos?.length && (
          <Stack spacing={1.5} sx={{ mt: 3 }}>
            {aviso.puntos.map((p) => (
              <Stack key={p} direction="row" spacing={1.25} alignItems="flex-start">
                <CheckCircleRounded sx={{ fontSize: 17, color: ac(), mt: '2px', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: alpha(TXT, 0.78) }}>{p}</Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {/* ── Acciones ──────────────────────────────────────────────────────── */}
        <Stack
          direction={chico ? 'column-reverse' : 'row'}
          spacing={1.25}
          justifyContent="center"
          sx={{ mt: 4 }}
        >
          {/* Cerrar SIEMPRE está, y en móvil queda abajo de todo para que el pulgar no
              lo toque sin querer antes de leer. */}
          <Button
            onClick={() => onCerrar(false)}
            sx={{
              ...botonSx, fontWeight: 700, color: alpha(TXT, 0.75),
              border: `1px solid ${alpha('#fff', 0.16)}`,
              '&:hover': { borderColor: alpha('#fff', 0.3), bgcolor: alpha('#fff', 0.04) },
            }}
          >{aviso.cerrar ?? 'Entendido'}</Button>

          {aviso.cta && (
            <Button
              onClick={() => onCerrar(aviso.cta?.accion === 'gustavo', aviso.cta?.href ?? undefined)}
              sx={{
                ...botonSx, fontWeight: 800, color: '#0d0f0e', bgcolor: acL(),
                boxShadow: `0 12px 34px ${em(0.3)}`,
                '&:hover': { bgcolor: ac(), boxShadow: `0 14px 40px ${em(0.4)}` },
              }}
            >{aviso.cta.texto}</Button>
          )}
        </Stack>
      </MBox>
    </Box>
  );
}
